import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Shield, HardDrive, Plug, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

interface SecuritySettings {
  allowed_mime_types: string[];
  max_upload_mb: number;
}

interface StorageSettings {
  provider: string;
  bucket: string | null;
  region: string | null;
  base_prefix: string | null;
  public_cdn_base: string | null;
}

interface MetaApiSettings {
  enabled: boolean;
  api_key: string | null;
  webhook_url: string | null;
}

interface Settings {
  id: string;
  security: SecuritySettings;
  storage: StorageSettings;
  meta_api: MetaApiSettings;
  updated_at: string;
}

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [mimeTypes, setMimeTypes] = useState('');
  const [maxUpload, setMaxUpload] = useState(10);
  const [storageProvider, setStorageProvider] = useState('local');
  const [bucket, setBucket] = useState('');
  const [region, setRegion] = useState('');
  const [basePrefix, setBasePrefix] = useState('');
  const [cdnBase, setCdnBase] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      
      // Populate form
      setMimeTypes(response.data.security.allowed_mime_types.join(', '));
      setMaxUpload(response.data.security.max_upload_mb);
      setStorageProvider(response.data.storage.provider);
      setBucket(response.data.storage.bucket || '');
      setRegion(response.data.storage.region || '');
      setBasePrefix(response.data.storage.base_prefix || '');
      setCdnBase(response.data.storage.public_cdn_base || '');
    } catch (error: any) {
      toast.error('Failed to load settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        security: {
          allowed_mime_types: mimeTypes.split(',').map(t => t.trim()).filter(t => t),
          max_upload_mb: maxUpload,
        },
        storage: {
          provider: storageProvider,
          bucket: bucket || null,
          region: region || null,
          base_prefix: basePrefix || null,
          public_cdn_base: cdnBase || null,
        },
      };

      await axios.patch(`${API}/settings`, updateData);
      toast.success('Settings updated successfully!');
      fetchSettings();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
          Global Settings
        </h2>
        <p className="text-gray-600">Configure system-wide settings and security policies</p>
      </div>

      <form onSubmit={handleSaveSettings} className="space-y-6">
        {/* Security Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Security Settings</h3>
                <p className="text-sm text-gray-600">Configure file upload security and restrictions</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Allowed MIME Types
              </label>
              <textarea
                value={mimeTypes}
                onChange={(e) => setMimeTypes(e.target.value)}
                rows={3}
                placeholder="image/jpeg, image/png, application/pdf"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="mime-types-input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Comma-separated list of allowed file types for uploads
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Upload Size (MB)
              </label>
              <input
                type="number"
                value={maxUpload}
                onChange={(e) => setMaxUpload(parseInt(e.target.value))}
                min={1}
                max={100}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="max-upload-input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum file size allowed for uploads (1-100 MB)
              </p>
            </div>
          </div>
        </div>

        {/* Storage Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <HardDrive className="text-blue-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Storage Settings</h3>
                <p className="text-sm text-gray-600">Configure file storage provider and locations</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Storage Provider
              </label>
              <select
                value={storageProvider}
                onChange={(e) => setStorageProvider(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="storage-provider-select"
              >
                <option value="local">Local Storage</option>
                <option value="s3">Amazon S3</option>
              </select>
            </div>

            {storageProvider === 's3' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    S3 Bucket Name
                  </label>
                  <input
                    type="text"
                    value={bucket}
                    onChange={(e) => setBucket(e.target.value)}
                    placeholder="my-app-uploads"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="bucket-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AWS Region
                  </label>
                  <input
                    type="text"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="us-east-1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="region-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base Prefix (Optional)
                  </label>
                  <input
                    type="text"
                    value={basePrefix}
                    onChange={(e) => setBasePrefix(e.target.value)}
                    placeholder="uploads/"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="base-prefix-input"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Folder path prefix for all uploaded files
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Public CDN Base URL (Optional)
                  </label>
                  <input
                    type="text"
                    value={cdnBase}
                    onChange={(e) => setCdnBase(e.target.value)}
                    placeholder="https://cdn.example.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="cdn-base-input"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    CloudFront or custom CDN URL for serving files
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Meta API Settings (Placeholder) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Plug className="text-purple-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Meta API Integration</h3>
                <p className="text-sm text-gray-600">Configure external API integrations (Coming Soon)</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <AlertCircle className="text-gray-400 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-gray-700">Integration Not Available</p>
                <p className="text-sm text-gray-600 mt-1">
                  Meta API integration settings will be available in a future release.
                  This section is reserved for webhook configurations and API credentials.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>

      {/* Last Updated */}
      {settings && (
        <div className="mt-6 text-center text-sm text-gray-500">
          Last updated: {new Date(settings.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
